import { computed, onScopeDispose, ref, shallowRef, watch } from 'vue';
import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';
import { ReservationsService } from '@/api/services/reservations.service';
import type { ISeatReservationWaitlistDto } from '@/api/services/reservations.service';
import type { IReservationWaitlist } from '@/interfaces/IReservationWaitlist';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useReservationsStore } from '@/stores/reservations.store';

export type TReservationWaitlistAction = 'notify' | 'seat' | 'cancel';

/** "45 minutes ago" / "3 hours ago" / "2 days ago" in the admin locale. */
export function relativeMinutesLabel(minutes: number, locale: string): string {
  const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
  const elapsed = Math.max(0, Math.floor(minutes));
  if (elapsed < 60) return format.format(-elapsed, 'minute');
  if (elapsed < 1440) return format.format(-Math.floor(elapsed / 60), 'hour');
  return format.format(-Math.floor(elapsed / 1440), 'day');
}

export function reservationWaitlistTiming(
  entry: Pick<
    IReservationWaitlist,
    'createdAt' | 'quotedWaitMin' | 'notifiedAt'
  >,
  now: Date
) {
  const elapsed = (value: string) =>
    Math.max(0, now.getTime() - Date.parse(value));
  const waitMs = elapsed(entry.createdAt);
  const quote = entry.quotedWaitMin;
  const overdueMs = quote == null ? 0 : Math.max(0, waitMs - quote * 60_000);
  return {
    waitingMinutes: Math.floor(waitMs / 60_000),
    overdueMinutes: Math.floor(overdueMs / 60_000),
    notifiedMinutes:
      entry.notifiedAt == null
        ? null
        : Math.floor(elapsed(entry.notifiedAt) / 60_000),
    tone:
      overdueMs > 0
        ? 'danger'
        : quote != null && waitMs >= quote * 60_000 * 0.8
        ? 'warning'
        : 'neutral',
  };
}

export function useReservationsWaitlist() {
  const restaurant = useRestaurantStore();
  const store = useReservationsStore();
  const visibility = useDocumentVisibility();
  const rows = shallowRef<IReservationWaitlist[]>([]);
  const loading = ref(false);
  const ready = ref(false);
  const error = shallowRef<unknown>(null);
  const busy = ref<Record<number, TReservationWaitlistAction>>({});
  const actionErrors = shallowRef<Record<number, TReservationWaitlistAction>>(
    {}
  );
  let sequence = 0;
  let generation = 0;
  let disposed = false;
  const key = () =>
    `${restaurant.restaurant?.id}:${restaurant.selectedBranch?.id}`;
  const active = (row: IReservationWaitlist) =>
    row.status === 'waiting' || row.status === 'notified';
  const inHouse = computed(() =>
    rows.value.filter((row) => row.kind === 'walk_in' && active(row))
  );
  const online = computed(() =>
    rows.value.filter(
      (row) =>
        row.kind === 'online' && (active(row) || row.status === 'expired')
    )
  );
  const waitingCount = computed(
    () => rows.value.filter((row) => row.status === 'waiting').length
  );

  async function load() {
    const restaurantId = restaurant.restaurant?.id;
    const branchId = restaurant.selectedBranch?.id;
    if (!restaurantId || !branchId || disposed || restaurant.effectiveTier < 2)
      return;
    const request = ++sequence;
    const requestKey = key();
    loading.value = true;
    error.value = null;
    try {
      const response = await ReservationsService.getWaitlist(
        restaurantId,
        branchId
      );
      if (disposed || request !== sequence || requestKey !== key()) return;
      rows.value = response;
      ready.value = true;
    } catch (cause) {
      if (!disposed && request === sequence && requestKey === key())
        error.value = cause;
    } finally {
      if (!disposed && request === sequence && requestKey === key())
        loading.value = false;
    }
  }

  async function mutate<T>(
    entry: IReservationWaitlist,
    action: TReservationWaitlistAction,
    run: () => Promise<T>,
    updatedEntry: (result: T) => IReservationWaitlist
  ): Promise<T | null> {
    if (
      disposed ||
      busy.value[entry.id] ||
      !active(entry) ||
      entry.restaurantId !== restaurant.restaurant?.id ||
      entry.branchId !== restaurant.selectedBranch?.id
    )
      return null;
    const requestKey = key();
    const requestGeneration = generation;
    const current = () =>
      !disposed && requestKey === key() && requestGeneration === generation;
    busy.value = { ...busy.value, [entry.id]: action };
    const errors = { ...actionErrors.value };
    delete errors[entry.id];
    actionErrors.value = errors;
    try {
      const result = await run();
      if (!current()) return null;
      ++sequence;
      const changed = updatedEntry(result);
      rows.value = rows.value.map((row) =>
        row.id === entry.id ? changed : row
      );
      loading.value = false;
      store.revision += 1;
      return result;
    } catch (cause) {
      if (!current()) return null;
      actionErrors.value = { ...actionErrors.value, [entry.id]: action };
      throw cause;
    } finally {
      if (current()) {
        const next = { ...busy.value };
        delete next[entry.id];
        busy.value = next;
      }
    }
  }
  const notify = (entry: IReservationWaitlist) =>
    mutate(
      entry,
      'notify',
      () => ReservationsService.notifyWaitlist(entry.id),
      (result) => result
    );
  const cancel = (entry: IReservationWaitlist) =>
    mutate(
      entry,
      'cancel',
      () => ReservationsService.cancelWaitlist(entry.id),
      (result) => result
    );
  async function seat(
    entry: IReservationWaitlist,
    dto: ISeatReservationWaitlistDto
  ) {
    const result = await mutate(
      entry,
      'seat',
      () => ReservationsService.seatWaitlist(entry.id, dto),
      (response) => response.entry
    );
    if (result) {
      store.patchReservation(result.reservation);
      void store.refreshActive();
    }
    return result;
  }
  watch(
    key,
    () => {
      ++sequence;
      ++generation;
      rows.value = [];
      ready.value = false;
      loading.value = false;
      error.value = null;
      busy.value = {};
      actionErrors.value = {};
      void load();
    },
    { immediate: true }
  );
  watch(
    () => store.revision,
    () => {
      if (visibility.value === 'visible') void load();
    }
  );
  watch(visibility, (value) => {
    if (value === 'visible') void load();
  });
  useIntervalFn(() => {
    if (visibility.value === 'visible') void load();
  }, 60_000);
  onScopeDispose(() => {
    disposed = true;
    ++sequence;
    ++generation;
  });
  return {
    rows,
    inHouse,
    online,
    waitingCount,
    loading,
    ready,
    error,
    busy,
    actionErrors,
    load,
    notify,
    cancel,
    seat,
  };
}
